import { useState, useEffect } from "react";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import { downloadGHGInventoryTemplate, downloadBlankInventoryWorksheet } from "../utils/templateDownload";
import { useBusinessReports } from "../hooks/useDatabase";
import { SaveCalculationModal, HistoryViewer } from "../components/SaveCalculationModal";
import {Building2, Download, Zap, Flame, FileSpreadsheet, FileText, Save, History} from 'lucide-react';

export default function BusinessCalculator() {
  // Scope 1 inputs
  const [companyVehicles, setCompanyVehicles] = useState("");
  const [vehiclesMiles, setVehiclesMiles] = useState("");
  const [naturalGas, setNaturalGas] = useState("");
  const [refrigerantLeaks, setRefrigerantLeaks] = useState("");
  
  // Scope 2 inputs
  const [electricityKwh, setElectricityKwh] = useState("");
  const [purchasedSteam, setPurchasedSteam] = useState("");
  
  // Business details
  const [companyName, setCompanyName] = useState("");
  const [reportPeriod, setReportPeriod] = useState("");
  
  const [scope1Total, setScope1Total] = useState<number | null>(null);
  const [scope2Total, setScope2Total] = useState<number | null>(null);
  const [totalEmissions, setTotalEmissions] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<any>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { businessReports, loading, fetchBusinessReports, saveBusinessReport, deleteBusinessReport } = useBusinessReports();

  useEffect(() => {
    fetchBusinessReports();
  }, []);

  const calculateBusinessEmissions = () => {
    // Scope 1 calculations
    const vehicles = parseFloat(companyVehicles) || 0;
    const miles = parseFloat(vehiclesMiles) || 0;
    const vehicleEmissions = (vehicles * miles * 0.404 * 12) / 1000; // tons CO2
    
    const gas = parseFloat(naturalGas) || 0;
    const gasEmissions = (gas * 0.0053) / 1000; // therms to tons CO2
    
    const refrigerant = parseFloat(refrigerantLeaks) || 0;
    const refrigerantEmissions = refrigerant * 1.43; // kg to tons, GWP factor
    
    const scope1 = vehicleEmissions + gasEmissions + refrigerantEmissions;
    
    // Scope 2 calculations
    const electricity = parseFloat(electricityKwh) || 0;
    const electricityEmissions = (electricity * 0.92 * 12) / 1000; // tons CO2
    
    const steam = parseFloat(purchasedSteam) || 0;
    const steamEmissions = steam / 1000; // assuming input in kg CO2
    
    const scope2 = electricityEmissions + steamEmissions;
    
    const total = scope1 + scope2;
    
    setScope1Total(parseFloat(scope1.toFixed(2)));
    setScope2Total(parseFloat(scope2.toFixed(2)));
    setTotalEmissions(parseFloat(total.toFixed(2)));
    setBreakdown({
      vehicles: parseFloat(vehicleEmissions.toFixed(2)),
      gas: parseFloat(gasEmissions.toFixed(2)),
      refrigerant: parseFloat(refrigerantEmissions.toFixed(2)),
      electricity: parseFloat(electricityEmissions.toFixed(2)),
      steam: parseFloat(steamEmissions.toFixed(2)),
    });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Small Business Greenhouse Gas Calculator",
    "url": "https://www.greenhousegascalculator.com/business",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Calculate Scope 1 and Scope 2 emissions for small businesses. Track natural gas, fleet vehicles, and electricity usage to measure your company's carbon footprint.",
    "featureList": "Scope 1 emissions calculator, Scope 2 emissions calculator, Business carbon footprint, GHG inventory template, Natural gas emissions, Fleet vehicle tracking"
  };

  return (
    <>
    <div className="bg-white min-h-screen">
      <SEO
        title="Small Business GHG Calculator | Scope 1 & 2 Emissions Tracking"
        description="Free small business greenhouse gas calculator. Calculate Scope 1 and Scope 2 emissions from natural gas, fleet vehicles, and electricity. Download free GHG inventory template."
        keywords="small business GHG calculator, scope 1 emissions, scope 2 emissions, business carbon footprint, greenhouse gas inventory, company emissions calculator"
        canonical="https://www.greenhousegascalculator.com/business"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-10 h-10 text-[#2E8B57]" />
              <h1 className="text-4xl font-bold text-gray-900">
                Small Business GHG Calculator
              </h1>
            </div>
            <p className="text-xl text-gray-600 mb-8">
              Calculate your business's Scope 1 and Scope 2 emissions to understand your carbon footprint and identify reduction opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Scope 1 & 2 Emissions Calculator
            </h2>

            <div className="space-y-6 mb-8">
              {/* Business Details */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Company Name</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g., Green Tech Solutions"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Report Period</label>
                    <input
                      type="text"
                      value={reportPeriod}
                      onChange={(e) => setReportPeriod(e.target.value)}
                      placeholder="e.g., Q1 2025 or Annual 2024"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Scope 1 */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Flame className="w-6 h-6 text-orange-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Scope 1 Emissions</h3>
                    <p className="text-sm text-gray-600">
                      Direct emissions from owned or controlled sources
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Number of Company Vehicles</label>
                      <input
                        type="number"
                        value={companyVehicles}
                        onChange={(e) => setCompanyVehicles(e.target.value)}
                        placeholder="e.g., 5"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Avg. Miles/Vehicle/Month</label>
                      <input
                        type="number"
                        value={vehiclesMiles}
                        onChange={(e) => setVehiclesMiles(e.target.value)}
                        placeholder="e.g., 1200"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Natural Gas Usage (therms/month)</label>
                    <input
                      type="number"
                      value={naturalGas}
                      onChange={(e) => setNaturalGas(e.target.value)}
                      placeholder="e.g., 500"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Refrigerant Leaks (kg/year)</label>
                    <input
                      type="number"
                      value={refrigerantLeaks}
                      onChange={(e) => setRefrigerantLeaks(e.target.value)}
                      placeholder="e.g., 10"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Scope 2 */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="w-6 h-6 text-yellow-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Scope 2 Emissions</h3>
                    <p className="text-sm text-gray-600">
                      Indirect emissions from purchased energy
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Electricity Usage (kWh/month)</label>
                    <input
                      type="number"
                      value={electricityKwh}
                      onChange={(e) => setElectricityKwh(e.target.value)}
                      placeholder="e.g., 5000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Check your utility bill for monthly kWh</p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Purchased Steam/Heating (kg CO₂/year)</label>
                    <input
                      type="number"
                      value={purchasedSteam}
                      onChange={(e) => setPurchasedSteam(e.target.value)}
                      placeholder="Optional - if applicable"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={calculateBusinessEmissions}
                className="flex-1 bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
              >
                Calculate Total Emissions
              </button>
              <button
                onClick={() => setShowHistory(true)}
                className="px-6 bg-gray-100 text-gray-700 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center gap-2"
                title="View saved reports"
              >
                <History className="w-5 h-5" />
                History
              </button>
            </div>

            {totalEmissions !== null && scope1Total !== null && scope2Total !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-white p-6 rounded-xl border-2 border-[#2E8B57]">
                  <div className="text-center">
                    <p className="text-gray-600 mb-2">Total Annual Emissions</p>
                    <p className="text-4xl font-bold text-[#2E8B57]">
                      {totalEmissions.toFixed(2)} tons CO₂e
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                    <h3 className="font-semibold text-gray-900 mb-2">Scope 1 Total</h3>
                    <p className="text-3xl font-bold text-orange-600">{scope1Total} tons</p>
                    <div className="mt-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicles:</span>
                        <span className="font-medium">{breakdown.vehicles} tons</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Natural Gas:</span>
                        <span className="font-medium">{breakdown.gas} tons</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Refrigerants:</span>
                        <span className="font-medium">{breakdown.refrigerant} tons</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                    <h3 className="font-semibold text-gray-900 mb-2">Scope 2 Total</h3>
                    <p className="text-3xl font-bold text-yellow-600">{scope2Total} tons</p>
                    <div className="mt-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Electricity:</span>
                        <span className="font-medium">{breakdown.electricity} tons</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Steam/Heating:</span>
                        <span className="font-medium">{breakdown.steam} tons</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Equivalencies Visualization */}
      {totalEmissions !== null && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Your Business Impact in Context
              </h2>
              <p className="text-lg text-gray-600">
                Real-world equivalencies to help understand your carbon footprint
              </p>
            </div>
            <EquivalenciesVisualizer emissions={totalEmissions} />
          </div>
        </section>
      )}

      {/* Free Template CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Export Results */}
          {totalEmissions !== null && (
            <div className="bg-gradient-to-r from-[#2E8B57] to-[#267347] rounded-2xl p-8 text-white mb-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    Export Your GHG Inventory Report
                  </h2>
                  <p className="text-green-100">
                    Download a comprehensive PDF report with your emissions breakdown and recommendations
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSaveModal(true)}
                    className="bg-white text-[#2E8B57] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    <Save className="w-5 h-5" />
                    Save Report
                  </button>
                  <button
                    onClick={() => exportToPDF({
                      title: "Small Business GHG Inventory Report",
                      totalEmissions: totalEmissions,
                      breakdown: breakdown || {},
                      scope1: scope1Total || 0,
                      scope2: scope2Total || 0,
                      calculationType: "business",
                    })}
                    className="bg-white text-[#2E8B57] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    <Download className="w-5 h-5" />
                    Export PDF
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Template Downloads */}
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Free GHG Inventory Templates
              </h2>
              <p className="text-lg text-gray-600">
                Download professional templates to track and manage your emissions data
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Excel Template */}
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#2E8B57] transition-all hover:shadow-lg">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <FileSpreadsheet className="w-8 h-8 text-[#2E8B57]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Excel Inventory Template
                    </h3>
                    <p className="text-gray-600">
                      Comprehensive spreadsheet with pre-built formulas, emission factors, and automatic calculations for Scope 1 & 2 emissions.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ Pre-filled emission factors (EPA approved)</li>
                    <li>✓ Automatic calculation formulas</li>
                    <li>✓ Scope 1 & 2 breakdown sections</li>
                    <li>✓ Emissions intensity metrics</li>
                    <li>✓ Year-over-year tracking</li>
                  </ul>
                </div>
                
                <button
                  onClick={downloadGHGInventoryTemplate}
                  className="w-full bg-[#2E8B57] text-white py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download Excel Template
                </button>
              </div>
              
              {/* Blank Worksheet */}
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#2E8B57] transition-all hover:shadow-lg">
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FileText className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Blank Inventory Worksheet
                    </h3>
                    <p className="text-gray-600">
                      Simple printable worksheet for manual data collection and preliminary calculations before digital entry.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Best for:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ Field data collection</li>
                    <li>✓ Team collaboration offline</li>
                    <li>✓ Initial scoping assessment</li>
                    <li>✓ Training and workshops</li>
                    <li>✓ Quick manual estimates</li>
                  </ul>
                </div>
                
                <button
                  onClick={downloadBlankInventoryWorksheet}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download Worksheet
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            Understanding Scope 1 & 2 Emissions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                What are Scope 1 Emissions?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Scope 1 emissions are direct greenhouse gas emissions from sources that are owned or controlled by your organization. This includes emissions from combustion in owned or controlled boilers, furnaces, vehicles, as well as emissions from chemical production in owned or controlled process equipment.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                What are Scope 2 Emissions?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Scope 2 emissions are indirect emissions from the generation of purchased energy consumed by your organization. This includes electricity, steam, heat, or cooling purchased from utility providers. Although the emissions physically occur at the facility where they are generated, they are attributed to your organization because you purchased the energy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
      
    <SaveCalculationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={async (name, notes) => {
          if (totalEmissions !== null && scope1Total !== null && scope2Total !== null && breakdown) {
            await saveBusinessReport({
              companyName: companyName || name,
              reportPeriod: reportPeriod || 'Current',
              scope1Emissions: scope1Total * 1000, // Convert tons to kg
              scope2Emissions: scope2Total * 1000,
              scope3Emissions: 0,
              totalEmissions: totalEmissions * 1000,
              detailedBreakdown: breakdown,
              inputs: {
                companyVehicles,
                vehiclesMiles,
                naturalGas,
                refrigerantLeaks,
                electricityKwh,
                purchasedSteam
              }
            });
          }
        }}
      />

      <HistoryViewer
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        calculations={businessReports}
        loading={loading}
        onDelete={deleteBusinessReport}
        onLoad={(report) => {
          if (report.inputs) {
            setCompanyName(report.companyName || '');
            setReportPeriod(report.reportPeriod || '');
            setCompanyVehicles(report.inputs.companyVehicles || '');
            setVehiclesMiles(report.inputs.vehiclesMiles || '');
            setNaturalGas(report.inputs.naturalGas || '');
            setRefrigerantLeaks(report.inputs.refrigerantLeaks || '');
            setElectricityKwh(report.inputs.electricityKwh || '');
            setPurchasedSteam(report.inputs.purchasedSteam || '');
            setScope1Total(report.scope1Emissions / 1000);
            setScope2Total(report.scope2Emissions / 1000);
            setTotalEmissions(report.totalEmissions / 1000);
            setBreakdown(report.detailedBreakdown);
          }
        }}
      />
    </>
  );
}
